import ReportProblem from '../components/ReportProblem';
import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const SECTIONS = [
  {
    title: 'Getting Started',
    items: [
      {
        q: 'How do I upload a lab report?',
        a: 'Click "Upload Report" in the sidebar. You can attach a PDF or take a photo of your printed results. We support reports from most major labs (Quest, LabCorp, hospital labs, etc.). Your file is encrypted in transit and stored securely.',
      },
      {
        q: 'What file types are supported?',
        a: 'PDF is recommended for best accuracy. JPG, PNG, and HEIC image files are also supported. Make sure the text is clear and not cut off.',
      },
      {
        q: 'How long does analysis take?',
        a: 'Most reports are fully analyzed within 15–30 seconds. Complex reports with many panels may take up to a minute.',
      },
    ],
  },
  {
    title: 'Understanding Your Results',
    items: [
      {
        q: 'What do the color indicators mean?',
        a: '"Normal" (green) means a value is within the standard reference range. "Watch" (yellow) means it\'s slightly outside the range and worth monitoring. "Flag" (red) means it\'s notably out of range — discuss with your doctor.',
      },
      {
        q: 'Are the AI explanations medical advice?',
        a: 'No. HealthClear AI provides educational explanations to help you understand your results and have better conversations with your healthcare provider. Always follow your doctor\'s guidance for diagnosis and treatment.',
      },
      {
        q: 'Why might my reference ranges differ from my lab\'s ranges?',
        a: 'Reference ranges can vary by lab, methodology, and population. HealthClear AI uses commonly accepted ranges, but your specific lab\'s ranges (printed on your report) take precedence.',
      },
    ],
  },
  {
    title: 'Trends & History',
    items: [
      {
        q: 'How do I track changes over time?',
        a: 'After uploading two or more reports that include the same biomarker, the Trends page will automatically chart how that value has changed. You can filter by biomarker and date range.',
      },
      {
        q: 'How do I delete a report?',
        a: 'Open the analysis for that report, scroll to the bottom, and use the "Delete Report" option. This permanently removes it from your history.',
      },
    ],
  },
  {
    title: 'Premium Features',
    items: [
      {
        q: 'What is included in the Starter plan?',
        a: 'Starter includes unlimited lab uploads, full AI analysis, trends tracking, and appointment prep notes. It\'s designed for people who regularly get bloodwork or other labs.',
      },
      {
        q: 'What does the Active plan add?',
        a: 'Active adds Medication Check (interactions & explanations), Care Gaps analysis (screening reminders), and priority support. Best for those managing chronic conditions or multiple medications.',
      },
      {
        q: 'Can I try premium features before subscribing?',
        a: 'Yes — new accounts get a free trial period with access to all features. You\'ll see a banner in the app when the trial is ending.',
      },
    ],
  },
  {
    title: 'Account & Privacy',
    items: [
      {
        q: 'Is my health data secure?',
        a: 'Yes. All data is encrypted at rest and in transit. We follow HIPAA-aligned security practices including automatic session timeouts, audit logging, and secure cloud storage. We never sell your health data.',
      },
      {
        q: 'Can I share my report with my doctor?',
        a: 'Yes — open any analysis and click the "Share" button to generate a secure link. The link expires after 7 days and can be revoked from your report\'s settings.',
      },
      {
        q: 'How do I delete my account?',
        a: 'Go to Profile & Settings, scroll to the bottom, and click "Delete my account." This permanently deletes all your data, including reports and analysis history.',
      },
      {
        q: 'How do I update my email or password?',
        a: 'Go to Profile & Settings. Update your name, email, and health background in the form, then click Save. To change your password, use "Forgot password" on the login screen.',
      },
    ],
  },
  {
    title: 'Mobile App',
    items: [
      {
        q: 'Is there a mobile app?',
        a: 'Yes! HealthClear AI is available on iOS (App Store) and Android (Google Play). Search for "HealthClear AI" or visit healthclearai.ai from your phone.',
      },
      {
        q: 'Can I use the same account on web and mobile?',
        a: 'Yes — your account, reports, and subscription are shared across all platforms. Sign in with the same email and password.',
      },
    ],
  },
];

function AccordionItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 dark:border-slate-700 last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 py-4 text-left"
      >
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{q}</span>
        {open
          ? <ChevronDownIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
          : <ChevronRightIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
        }
      </button>
      {open && (
        <p className="text-sm text-slate-500 dark:text-slate-400 pb-4 leading-relaxed pr-6">{a}</p>
      )}
    </div>
  );
}

export default function HelpPage() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-1">Help & FAQ</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          Common questions about using HealthClear AI.
        </p>
      </div>

      <div className="space-y-6">
        {SECTIONS.map(({ title, items }) => (
          <div key={title} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 px-5">
            <h2 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest pt-5 pb-2">{title}</h2>
            {items.map(({ q, a }) => (
              <AccordionItem key={q} q={q} a={a} />
            ))}
          </div>
        ))}
      </div>

      {/* Above the mailto on purpose. The mailto has collected nothing in
          months — it asks someone mid-frustration to leave the app and
          compose an email. This asks for one sentence. */}
      <ReportProblem />

      <div className="mt-8 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-xl p-5 text-sm text-slate-600 dark:text-slate-300">
        <p className="font-semibold text-sky-700 dark:text-sky-300 mb-1">Still have questions?</p>
        <p>
          Email us at{' '}
          <a href="mailto:support@healthclearai.ai" className="text-sky-600 dark:text-sky-400 font-medium hover:underline">
            support@healthclearai.ai
          </a>
          {' '}and we'll get back to you within one business day.
        </p>
      </div>
    </div>
  );
}
